/**
 * libSQL native vector strategy.
 *
 * libSQL ships vector search in core — no extension to load — so this
 * strategy is wired unconditionally by `createLibsqlBackend`. It backs each
 * `(nodeKind, fieldPath)` with its own `F32_BLOB(N)` table (the spike proved
 * the dimension MUST live in the column type for `libsql_vector_idx` to
 * build), brute-forces with `vector_distance_cos` / `vector_distance_l2`, and
 * accelerates with DiskANN (`libsql_vector_idx` + `vector_top_k`) when the
 * slot declares an approximate index.
 *
 * Metric support is `cosine` + `l2` (no `inner_product`), matching sqlite-vec
 * and advertised as data on `capabilities`. The generic `"hnsw"` index intent
 * (the portable "give me an ANN index" signal that `embedding()` defaults to)
 * is realized here as libSQL's DiskANN index; `"ivfflat"` is not supported and
 * a slot declaring it is materialized brute-force-only.
 *
 * The `pgvectorStrategy` and `sqliteVecStrategy` siblings follow the same
 * `VectorStrategy` contract for their engines.
 */
import { type StrategyTableContribution } from "../../../backend/table-contribution";
import {
  type DeleteEmbeddingParams,
  type UpsertEmbeddingBatchParams,
  type UpsertEmbeddingParams,
  type VectorCapabilities,
  type VectorMetric,
  type VectorSearchParams,
} from "../../../backend/types";
import { sql, type SqlFragment } from "../../sql-fragment";
import {
  assertFiniteEmbedding,
  quoteIdentifier,
  VECTOR_CONTRIBUTION_PREFIX,
  vectorMinScoreCondition,
  vectorPhysicalName,
  vectorScoreExpression,
  type VectorSlot,
  type VectorStrategy,
} from "../vector-strategy";
import { vectorPageClause } from "./pagination";

/** Physical-name prefixes for the per-field table and its DiskANN index. */
const TABLE_PREFIX = "tg_vec";
const INDEX_PREFIX = "tg_vecidx";

/**
 * libSQL `F32_BLOB` caps at 65,536 dimensions (per Turso docs / spike §2).
 */
const LIBSQL_MAX_DIMENSIONS = 65_536;

const LIBSQL_CAPABILITIES: VectorCapabilities = {
  supported: true,
  metrics: ["cosine", "l2"],
  // "hnsw" = portable ANN intent, realized as DiskANN. "none" = brute-force.
  indexTypes: ["hnsw", "none"],
  maxDimensions: LIBSQL_MAX_DIMENSIONS,
  // `vector_top_k` is a table function: no filter pushdown, no way to re-enter
  // the index. A filtered DiskANN search over-fetches by
  // CANDIDATE_FILTER_OVERFETCH and filters afterwards, so it under-fills once
  // more than the headroom is filtered out. Unlike sqlite-vec (pushdown) and
  // pgvector >= 0.8 (iterative scan), libSQL has no recovery at all.
  filteredApproximateSearch: {
    mode: "post-filter",
    guaranteesFullPage: false,
  },
  // `vector_top_k(idx, q, k)` is a table function whose only parameters are
  // the index, the query vector, and `k`. DiskANN's search-list width
  // (`search_l`) is fixed when the index is created by
  // `libsql_vector_idx(...)` and cannot be varied per query, so there is no
  // per-search frontier for `efSearch` to set.
  searchFrontierTuning: {
    tunable: false,
    reason:
      "`vector_top_k` takes only (index, query, k); DiskANN's search_l is fixed at index-creation time",
  },
};

/** Whether a slot's declared index type maps to a real libSQL ANN index. */
function usesAnnIndex(slot: VectorSlot): boolean {
  return slot.indexType === "hnsw";
}

/** libSQL metric token accepted by `libsql_vector_idx(col, 'metric=…')`. */
function libsqlMetricToken(metric: VectorMetric): string {
  switch (metric) {
    case "cosine": {
      return "cosine";
    }
    case "l2": {
      return "l2";
    }
    case "inner_product": {
      throw new Error(
        "libSQL vector search does not support inner_product. Use 'cosine' or 'l2'.",
      );
    }
    default: {
      const _exhaustive: never = metric;
      throw new Error(`Unsupported vector metric: ${String(_exhaustive)}`);
    }
  }
}

/**
 * Formats an embedding as the text argument to `vector32(...)`. The value is
 * bound as a parameter (not interpolated) — `vector32` parses it into the
 * F32 blob. Finiteness is validated first so a NaN names its index.
 */
function vector32Literal(
  embedding: readonly number[],
  name: string,
): SqlFragment {
  assertFiniteEmbedding(embedding, name);
  return sql`vector32(${`[${embedding.join(",")}]`})`;
}

function distanceExpression(
  embeddingColumn: SqlFragment,
  queryEmbedding: readonly number[],
  metric: VectorMetric,
): SqlFragment {
  const query = vector32Literal(queryEmbedding, "queryEmbedding");
  switch (metric) {
    case "cosine": {
      return sql`vector_distance_cos(${embeddingColumn}, ${query})`;
    }
    case "l2": {
      return sql`vector_distance_l2(${embeddingColumn}, ${query})`;
    }
    case "inner_product": {
      throw new Error(
        "libSQL vector search does not support inner_product. Use 'cosine' or 'l2'.",
      );
    }
    default: {
      const _exhaustive: never = metric;
      throw new Error(`Unsupported vector metric: ${String(_exhaustive)}`);
    }
  }
}

function tableName(
  graphId: string,
  nodeKind: string,
  fieldPath: string,
): string {
  return vectorPhysicalName(TABLE_PREFIX, graphId, nodeKind, fieldPath);
}

export const libsqlVectorStrategy: VectorStrategy = {
  name: "libsql-native",
  capabilities: LIBSQL_CAPABILITIES,

  tableName,

  ownedTables(slot): readonly StrategyTableContribution[] {
    const table = tableName(slot.graphId, slot.nodeKind, slot.fieldPath);
    const name = quoteIdentifier(table);

    // No standalone graph_id index: the PRIMARY KEY (graph_id, node_id) already
    // covers `WHERE graph_id = ?` via its leading column, so a separate index
    // would be pure write amplification.
    const createDdl = [
      `CREATE TABLE IF NOT EXISTS ${name} (
  "graph_id" TEXT NOT NULL,
  "node_id" TEXT NOT NULL,
  "embedding" F32_BLOB(${slot.dimensions}) NOT NULL,
  "created_at" TEXT NOT NULL,
  "updated_at" TEXT NOT NULL,
  PRIMARY KEY ("graph_id", "node_id")
);`,
    ];

    if (usesAnnIndex(slot)) {
      // The DiskANN index DDL is deterministic raw SQL; fold it into the
      // contribution so materialization creates table + index together.
      createDdl.push(libsqlVectorIndexDdl(table, slot));
    }

    return [
      {
        scope: "graph",
        logicalName: `${VECTOR_CONTRIBUTION_PREFIX}:${slot.nodeKind}.${slot.fieldPath}`,
        owner: "libsql-native",
        tableName: table,
        createDdl,
        runtimeEnsure: true,
      },
    ];
  },

  buildUpsert(
    slot,
    params: UpsertEmbeddingParams,
    timestamp,
  ): readonly SqlFragment[] {
    const table = sql.identifier(
      tableName(slot.graphId, slot.nodeKind, slot.fieldPath),
    );
    const value = vector32Literal(params.embedding, "embedding");
    return [
      sql`
        INSERT INTO ${table} ("graph_id", "node_id", "embedding", "created_at", "updated_at")
        VALUES (${params.graphId}, ${params.nodeId}, ${value}, ${timestamp}, ${timestamp})
        ON CONFLICT ("graph_id", "node_id")
        DO UPDATE SET "embedding" = ${value}, "updated_at" = ${timestamp}
      `,
    ];
  },

  buildUpsertBatch(
    slot,
    params: UpsertEmbeddingBatchParams,
    timestamp,
  ): readonly SqlFragment[] {
    const table = sql.identifier(
      tableName(slot.graphId, slot.nodeKind, slot.fieldPath),
    );
    const valueRows = sql.join(
      params.rows.map(
        (row) =>
          sql`(${params.graphId}, ${row.nodeId}, ${vector32Literal(row.embedding, "embedding")}, ${timestamp}, ${timestamp})`,
      ),
      sql`, `,
    );
    // `excluded."embedding"` reuses the row's already-converted F32_BLOB
    // value, so the multi-row form needs no per-row conversion in the
    // update arm (unlike buildUpsert's single bound value).
    return [
      sql`
        INSERT INTO ${table} ("graph_id", "node_id", "embedding", "created_at", "updated_at")
        VALUES ${valueRows}
        ON CONFLICT ("graph_id", "node_id")
        DO UPDATE SET "embedding" = excluded."embedding", "updated_at" = excluded."updated_at"
      `,
    ];
  },

  buildDelete(slot, params: DeleteEmbeddingParams): readonly SqlFragment[] {
    const table = sql.identifier(
      tableName(slot.graphId, slot.nodeKind, slot.fieldPath),
    );
    return [
      sql`
        DELETE FROM ${table}
        WHERE "graph_id" = ${params.graphId} AND "node_id" = ${params.nodeId}
      `,
    ];
  },

  buildDeleteBatch(
    slot,
    params: Omit<DeleteEmbeddingParams, "nodeId"> &
      Readonly<{ nodeIds: readonly string[] }>,
  ): readonly SqlFragment[] {
    if (params.nodeIds.length === 0) return [];
    const table = sql.identifier(
      tableName(slot.graphId, slot.nodeKind, slot.fieldPath),
    );
    return [
      sql`DELETE FROM ${table} WHERE "graph_id" = ${params.graphId} AND "node_id" IN (${sql.join(
        params.nodeIds.map((nodeId) => sql`${nodeId}`),
        sql`, `,
      )})`,
    ];
  },

  buildSearch(
    slot,
    params: VectorSearchParams,
    candidates?: SqlFragment,
  ): SqlFragment {
    const table = tableName(slot.graphId, slot.nodeKind, slot.fieldPath);
    const quoted = sql.identifier(table);
    const embeddingColumn = sql`${quoted}."embedding"`;
    const distance = distanceExpression(
      embeddingColumn,
      params.queryEmbedding,
      params.metric,
    );
    const score = vectorScoreExpression(distance, params.metric);

    if (usesAnnIndex(slot)) {
      // DiskANN: vector_top_k returns rowids from the per-field table; join
      // back, then scope by graph and compute the real distance/score.
      // `vector_top_k` is table-global, so single-graph deployments are exact;
      // multi-graph recall is bounded by the over-fetched k (documented).
      const indexName = libsqlIndexName(slot);
      const query = vector32Literal(params.queryEmbedding, "queryEmbedding");
      const conditions: SqlFragment[] = [
        sql`${quoted}."graph_id" = ${params.graphId}`,
      ];
      if (params.minScore !== undefined) {
        conditions.push(
          vectorMinScoreCondition(distance, params.metric, params.minScore),
        );
      }
      // `vector_top_k` is a table function with no filter pushdown, so a
      // candidate filter can only be applied AFTER the ANN retrieval.
      // Over-fetch the DiskANN k to leave headroom for filtered-out rows;
      // recall is bounded by the over-fetch, and the page under-fills once
      // more than the headroom is filtered out. Declared, not merely noted:
      // `capabilities.filteredApproximateSearch.mode === "post-filter"`.
      if (candidates !== undefined) {
        conditions.push(sql`${quoted}."node_id" IN (${candidates})`);
      }
      // `pageK` covers the requested page (`limit + offset`); the
      // candidate-filter over-fetch multiplies on top of it.
      const pageK = params.limit + (params.offset ?? 0);
      const annK =
        candidates === undefined ? pageK : pageK * CANDIDATE_FILTER_OVERFETCH;
      const pageClause = vectorPageClause(params.limit, params.offset);
      return sql`
        SELECT ${quoted}."node_id" AS node_id, ${score} AS score
        FROM vector_top_k(${indexName}, ${query}, ${annK})
        JOIN ${quoted} ON ${quoted}.rowid = id
        WHERE ${sql.join(conditions, sql` AND `)}
        ORDER BY ${distance} ASC
        ${pageClause}
      `;
    }

    // Brute-force scan.
    const conditions: SqlFragment[] = [
      sql`${quoted}."graph_id" = ${params.graphId}`,
    ];
    if (candidates !== undefined) {
      conditions.push(sql`${quoted}."node_id" IN (${candidates})`);
    }
    if (params.minScore !== undefined) {
      conditions.push(
        vectorMinScoreCondition(distance, params.metric, params.minScore),
      );
    }
    const pageClause = vectorPageClause(params.limit, params.offset);
    return sql`
      SELECT ${quoted}."node_id" AS node_id, ${score} AS score
      FROM ${quoted}
      WHERE ${sql.join(conditions, sql` AND `)}
      ORDER BY ${distance} ASC
      ${pageClause}
    `;
  },

  distanceExpression(embeddingColumn, queryEmbedding, metric) {
    return distanceExpression(embeddingColumn, queryEmbedding, metric);
  },

  buildCreateIndex(slot): SqlFragment | undefined {
    if (!usesAnnIndex(slot)) return undefined;
    return sql.raw(
      libsqlVectorIndexDdl(
        tableName(slot.graphId, slot.nodeKind, slot.fieldPath),
        slot,
      ),
    );
  },

  buildDropIndex(slot): SqlFragment | undefined {
    if (!usesAnnIndex(slot)) return undefined;
    const indexName = libsqlIndexName(slot);
    return sql.raw(`DROP INDEX IF EXISTS ${quoteIdentifier(indexName)}`);
  },

  buildDropStorage(slot): readonly string[] {
    const table = quoteIdentifier(
      tableName(slot.graphId, slot.nodeKind, slot.fieldPath),
    );
    const statements: string[] = [];
    // Drop the DiskANN index first so libSQL reclaims its shadow tables,
    // then the table itself.
    if (usesAnnIndex(slot)) {
      statements.push(
        `DROP INDEX IF EXISTS ${quoteIdentifier(libsqlIndexName(slot))}`,
      );
    }
    statements.push(`DROP TABLE IF EXISTS ${table}`);
    return statements;
  },
};

/**
 * DiskANN over-fetch multiplier applied when a candidate filter is present:
 * `vector_top_k` cannot pre-filter, so fetch `4k` neighbors and filter after.
 * Mirrors the hybrid facade's 4x over-fetch. Recall for the filtered search
 * is bounded by this headroom — if more than `3k` of the top `4k` neighbors
 * are filtered out, fewer than `k` rows return. That is the observable
 * meaning of `capabilities.filteredApproximateSearch.guaranteesFullPage`.
 */
const CANDIDATE_FILTER_OVERFETCH = 4;

function libsqlIndexName(slot: VectorSlot): string {
  return vectorPhysicalName(
    INDEX_PREFIX,
    slot.graphId,
    slot.nodeKind,
    slot.fieldPath,
  );
}

function libsqlVectorIndexDdl(table: string, slot: VectorSlot): string {
  const indexName = quoteIdentifier(libsqlIndexName(slot));
  const metric = libsqlMetricToken(slot.metric);
  return `CREATE INDEX IF NOT EXISTS ${indexName} ON ${quoteIdentifier(table)}(libsql_vector_idx("embedding", 'metric=${metric}'));`;
}
